import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SendHorizontal, Loader2, Plus, X, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { getBrowserId } from "@/lib/browserId";
import {
  createSession,
  getMessages,
  streamChat,
  listSessions,
} from "@/lib/api";

const ALLOWED_PARENTS = ["theepcregister.co.uk", "theepcman.co.uk", "chatepc.co.uk", "localhost", "emergentagent.com"];

function parentAllowed() {
  // If not framed (opened directly), allow only if own hostname is in allowlist
  const host = window.location.hostname.replace(/^www\./, "");
  if (window.top === window.self) {
    return ALLOWED_PARENTS.some((h) => host === h || host.endsWith("." + h));
  }
  const candidates = [];
  try {
    const ao = window.location.ancestorOrigins;
    if (ao && ao.length) {
      for (let i = 0; i < ao.length; i++) {
        try { candidates.push(new URL(ao[i]).hostname); } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }
  try {
    const p = new URLSearchParams(window.location.search).get("parent");
    if (p) candidates.push(p);
  } catch { /* ignore */ }
  try {
    if (document.referrer) candidates.push(new URL(document.referrer).hostname);
  } catch { /* ignore */ }

  for (const raw of candidates) {
    const h2 = String(raw || "").replace(/^www\./, "").toLowerCase();
    if (ALLOWED_PARENTS.some((h) => h2 === h || h2.endsWith("." + h))) return true;
  }
  return false;
}

export default function EmbedChat() {
  const [allowed] = useState(() => parentAllowed());
  const [userId] = useState(() => getBrowserId());
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pendingAssistant, setPendingAssistant] = useState("");
  const scrollRef = useRef(null);

  // Restore latest embed session (per browser)
  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const list = await listSessions(userId);
        const embedSession = list.find((s) => s.title?.startsWith("[embed] "));
        if (embedSession) {
          setSessionId(embedSession.id);
          const msgs = await getMessages(embedSession.id);
          setMessages(msgs);
        }
      } catch {
        /* fresh start */
      }
    })();
  }, [allowed, userId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pendingAssistant]);

  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center font-sans bg-white">
        <div className="max-w-sm">
          <Logo className="h-10 w-10 mx-auto" />
          <h1 className="mt-4 font-heading text-lg tracking-tight">
            Not available on this site
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            ChatEPC is not authorised to be embedded on this domain.
          </p>
          <a
            href="https://chatepc.co.uk/chat"
            className="inline-flex items-center gap-1 mt-4 text-sm text-epc-a"
          >
            Open ChatEPC <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    );
  }

  async function ensureSession(firstText) {
    if (sessionId) return sessionId;
    const created = await createSession(
      userId,
      "[embed] " + (firstText.slice(0, 50) || "chat")
    );
    setSessionId(created.id);
    return created.id;
  }

  async function handleNew() {
    setSessionId(null);
    setMessages([]);
    setPendingAssistant("");
  }

  async function handleSend(text) {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setPendingAssistant("");
    try {
      const sid = await ensureSession(content);
      setMessages((m) => [
        ...m,
        { id: `tmp_${Date.now()}`, role: "user", content, session_id: sid },
      ]);
      let acc = "";
      await streamChat({
        session_id: sid,
        user_id: userId,
        content,
        embed_mode: true,
        onDelta: (chunk) => {
          acc += chunk;
          setPendingAssistant(acc);
        },
        onDone: () => {
          setMessages((m) => [
            ...m,
            { id: `a_${Date.now()}`, role: "assistant", content: acc, session_id: sid },
          ]);
          setPendingAssistant("");
        },
        onError: (msg) => toast.error("Chat error: " + msg),
      });
    } catch (e) {
      toast.error(e.message || "Failed to send");
    } finally {
      setStreaming(false);
    }
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-white font-sans" data-testid="embed-chat">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 h-14 border-b border-border shrink-0">
        <Logo className="h-6 w-6" />
        <div className="font-heading text-sm tracking-tight">
          ChatEPC <span className="text-muted-foreground font-normal hidden sm:inline">· UK EPC assistant</span>
        </div>
        <button
          onClick={handleNew}
          data-testid="embed-new-chat"
          className="ml-auto text-muted-foreground hover:text-foreground text-xs inline-flex items-center gap-1"
          aria-label="New chat"
        >
          <Plus className="h-4 w-4" /> New
        </button>
        <button
          type="button"
          onClick={() => window.parent && window.parent.postMessage({ chatepc: "close" }, "*")}
          data-testid="embed-close"
          className="inline-flex items-center gap-1.5 h-9 px-3 border border-border bg-white hover:bg-neutral-50 text-sm text-foreground"
          aria-label="Close chat"
        >
          <X className="h-4 w-4" />
          <span>Close</span>
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4">
        {messages.length === 0 && !streaming && (
          <div className="space-y-2" data-testid="embed-empty">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Ask about your EPC</div>
            <div className="text-sm text-foreground/80">
              Try: "What is an EPC?" · "How do I improve my rating from D to C?" · "What is MEES?"
            </div>
          </div>
        )}
        <ul className="space-y-4">
          {messages.map((m) => (
            <Bubble key={m.id} role={m.role} content={m.content} />
          ))}
          {streaming && <Bubble role="assistant" content={pendingAssistant} streaming />}
        </ul>
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
        className="border-t border-border p-2 flex items-end gap-2 shrink-0"
      >
        <textarea
          data-testid="embed-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
          }}
          rows={1}
          placeholder="Ask about UK EPCs…"
          className="flex-1 resize-none bg-transparent outline-none px-2 py-2 text-sm border border-border max-h-32"
        />
        <Button
          type="submit"
          data-testid="embed-send"
          disabled={streaming || !input.trim()}
          className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-10"
        >
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
        </Button>
      </form>
      <div className="text-[10px] text-center text-muted-foreground py-1 border-t border-border">
        Powered by ChatEPC · UK EPC assistant
      </div>
    </div>
  );
}

function Bubble({ role, content, streaming = false }) {
  const isUser = role === "user";
  return (
    <li
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`embed-msg-${role}`}
    >
      <div
        className={`max-w-[90%] px-3 py-2 leading-relaxed text-sm ${
          isUser
            ? "bg-epc-a text-white"
            : "bg-neutral-100 text-foreground border border-border"
        }`}
      >
        {isUser ? (
          <div className={`msg-body whitespace-pre-wrap ${streaming ? "caret" : ""}`}>{content}</div>
        ) : (
          <div className={`msg-body ${streaming ? "caret" : ""}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-epc-a underline underline-offset-2 hover:text-epc-a/80 break-words"
                  />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </li>
  );
}
